import { useState } from "react";
import { useHistory, useParams } from "react-router";
import { useFormik, setNestedObjectValues } from "formik";
import { isValidPhoneMask } from "Utils/Phone";
import * as yup from "yup";
import {
  cancelPreBudget,
  completePreBudget,
  saveCartChanges,
} from "Requests/Partners/PreBudgets";
import { createToast } from "Utils/toastFunc";
import { isCPF } from "Services/schema/validations";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import { useItemsContext } from "./useItemsContext";
import { usePreOrderDetails } from "./usePreOrderDetails";

const nameValidation = yup
  .string()
  .matches(
    /^[\p{L}\s\-'.,]+$/u,
    "Nome deve conter apenas letras, espaços, hífens, aspas e pontos"
  );

const validatePhone = (phone?: string) => {
  if (phone) {
    const [ddiRaw, dddRaw, n1, n2] = formatPhoneNumberIntl(phone).split(" ");

    if (!ddiRaw || !dddRaw || !n1 || !n2) return false;

    return true;
  }

  return false;
};

export const personData = yup.object({
  name: nameValidation.required("Nome do paciente é obrigatório"),
  document: yup
    .string()
    .required("Documento obrigatório")
    .test("document", "CPF inválido", isCPF),
  doctor: nameValidation
    .nullable()
    .test(
      "doctor-validation",
      "Nome do médico deve ser válido",
      function (value) {
        if (!value || value.trim() === "") return true; // Campo vazio é válido
        return nameValidation.isValidSync(value);
      }
    ),
  crm: yup.string(),
  phone: yup
    .string()
    .required(" ")
    .test("phone", "Telefone inválido", (value) => isValidPhoneMask(value))
    .test("phone", "Telefone inválido", (value) => validatePhone(value)),
  mothers_full_name: nameValidation
    .nullable()
    .test("mother-validation", "Nome da mãe deve ser válido", function (value) {
      if (!value || value.trim() === "") return true; // Campo vazio é válido
      return nameValidation.isValidSync(value);
    }),
});

export type Values = yup.InferType<typeof personData>;

export type PersonInfo = {
  full_name: string;
  email: string;
  phone: string;
  doctor: string;
  crm: string;
  address: {
    zipcode: string;
    street: string;
    number: string;
  };
};

export const useCartDetails = () => {
  const [tab, setTab] = useState<"cart" | "info">("cart");
  const [loading, setLoading] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [editFields, setEditFields] = useState(false);

  const { items, setBlockUserInteraction, blockUserInteraction } =
    useItemsContext();

  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const preOderDetailsAPI = usePreOrderDetails(Number(id));

  let patientPhone = "";

  const {
    full_name,
    document,
    ddi,
    ddd,
    phone,
    doctor,
    crm,
    email,
    address,
    mothers_full_name,
  } = preOderDetailsAPI.data || {};

  let customDdi = "55";

  if (ddi) {
    customDdi = ddi.replace(/\D/g, "");
  }

  if (phone) {
    const phoneSliced = `${phone?.slice(0, -4)}-${phone?.slice(-4)}`;
    patientPhone = `+${customDdi} ${ddd} ${phoneSliced}`;
  }

  const handleSubmit = async () => {
    try {
      setBlockUserInteraction(true);
      setLoading(true);

      const validationErrors = await formik.validateForm();
      if (Object.keys(validationErrors).length > 0) {
        formik.setTouched(setNestedObjectValues(validationErrors, true));
        setEditFields(true);
        setTab("info");
        return;
      }
      const itemsInCart = items.map((item) => ({
        product_id: item.product_id,
        amount: item.amount,
      }));
      const values = formik.values as Values;
      const [nddi, nddd, n1, n2] = formatPhoneNumberIntl(values.phone).split(
        " "
      );

      const patient = {
        full_name: values.name,
        document: values.document,

        phone: `${n1}${n2}`,
        ddd: nddd,
        ddi: nddi.replace("+", ""),
        email: email || "",
        doctor: values.doctor || "",
        crm: values.crm || "",
        address: {
          zipcode: address?.zipcode || "",
          street: address?.street || "",
          number: address?.number || "",
        },
      };

      const cart = {
        cart: itemsInCart,
        track: null,
        patient,
      };

      await saveCartChanges(Number(id), cart);
      const response = await completePreBudget(Number(id));

      if (response.status === 202) {
        createToast(
          "success",
          "Orçamento criado com sucesso. Os itens estão sendo processados em segundo plano."
        );
        history.push(`/listagem-pre-orcamento/domiciliar`);
        return;
      }

      const { id: budgetId } = response.data;

      createToast("success", "Orçamento criado com sucesso!");
      history.push(`/ficha-de-atendimento-parceiro/${budgetId}/domiciliar`);
    } catch (error: any) {
      if (error.product_id) {
        const product = items.find(
          (item) => item.product_id === error.product_id
        );
        error.message.forEach((message: string) =>
          createToast("error", `${product?.product_name} - ${message}`)
        );

        return;
      }

      const { errors } = error?.response?.data;

      if (errors) {
        errors.forEach((errorMessage: string) => {
          createToast("error", errorMessage);
        });
      }
    } finally {
      setBlockUserInteraction(false);
      setLoading(false);
    }
  };

  const handleCancelPreBudget = async () => {
    try {
      setBlockUserInteraction(true);
      setLoading(true);
      await cancelPreBudget(Number(id));
      createToast("success", "Pré-orçamento cancelado com sucesso!");
      history.push("/listagem-pre-orcamento/domiciliar");
    } catch (error) {
      createToast("error", "Não foi possível cancelar o pré-orçamento");
    } finally {
      setBlockUserInteraction(false);
      setLoading(false);
    }
  };
  const onCancelBudget = async () => {
    try {
      await cancelPreBudget(Number(id));
      createToast("success", "Orçamento cancelado com sucesso!");
      history.push("/orcamentos/parceiro/domiciliar");
    } catch (error) {
      createToast("error", "Não foi possível cancelar o orçamento");
    }
  };

  const formik = useFormik<Values>({
    initialValues: {
      name: full_name || "",
      document: document || "",
      phone: patientPhone || "",
      doctor: doctor || "",
      crm: crm || "",
      mothers_full_name: mothers_full_name || "",
    },
    onSubmit: handleSubmit,
    enableReinitialize: true,
    validationSchema: personData,
  });

  return {
    formik,
    tab,
    setTab,
    blockUserInteraction,
    items,
    handleSubmit,
    handleCancelPreBudget,
    loading,
    cancelModal,
    setCancelModal,
    onCancelBudget,
    editFields,
    setEditFields,
  };
};
